import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, MessageCircle, X } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "@/i18n";
import { track, waLink } from "@/lib/api";

const sections = [
  { id: "how", key: "nav_how" },
  { id: "services", key: "nav_services" },
  { id: "trust", key: "nav_trust" },
  { id: "cities", key: "nav_cities" },
  { id: "faq", key: "nav_faq" },
];

export default function Header() {
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/#" + id);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const goBook = () => {
    track("book_click", { source: "header" });
    setOpen(false);
    navigate("/book");
  };

  const onWhatsApp = () => {
    track("whatsapp_click", { source: "header" });
    window.open(waLink("Hi CHELA! I'd like to book an extra pair of hands."), "_blank");
  };

  return (
    <header
      data-testid="main-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#FAF8F5]/90 border-b border-[#E5E0D8]" : "bg-transparent"
      }`}
    >
      <div className="chela-container flex items-center justify-between h-16 sm:h-20">
        <button data-testid="nav-logo" onClick={() => (location.pathname === "/" ? window.scrollTo({ top: 0, behavior: "smooth" }) : navigate("/"))} className="flex items-center">
          <Logo size="text-2xl sm:text-[28px]" />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {sections.map((s) => (
            <button
              key={s.id}
              data-testid={`nav-${s.id}`}
              onClick={() => goSection(s.id)}
              className="text-sm font-semibold text-[#3A3D45] hover:text-[#E05624] transition-colors"
            >
              {t(s.key)}
            </button>
          ))}
          <button
            data-testid="nav-partner"
            onClick={() => { setOpen(false); navigate("/partner"); }}
            className="text-sm font-semibold text-[#3A3D45] hover:text-[#E05624] transition-colors"
          >
            {t("nav_partner")}
          </button>
          <button data-testid="nav-join" onClick={() => { setOpen(false); navigate("/join"); }} className="text-sm font-semibold text-[#3A3D45] hover:text-[#E05624] transition-colors">
            {t("foot_join")}
          </button>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="h-9 px-3 rounded-full border border-[#E5E0D8] bg-white text-xs font-bold text-[#121316] hover:border-[#E05624] transition-colors"
          >
            {lang === "en" ? "हिंदी" : "EN"}
          </button>
          <button
            data-testid="whatsapp-btn"
            onClick={onWhatsApp}
            className="hidden sm:inline-flex items-center gap-2 h-9 px-3 rounded-full btn-whatsapp text-xs font-bold"
          >
            <MessageCircle size={15} /> {t("whatsapp")}
          </button>
          <button
            data-testid="book-a-chela-btn"
            onClick={goBook}
            className="hidden sm:inline-flex items-center h-10 px-5 rounded-full btn-saffron text-sm font-bold"
          >
            {t("book")}
          </button>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden h-10 w-10 flex items-center justify-center rounded-full border border-[#E5E0D8] bg-white"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#FAF8F5] border-b border-[#E5E0D8] animate-rise">
          <div className="chela-container py-4 flex flex-col gap-1">
            {sections.map((s) => (
              <button key={s.id} data-testid={`mnav-${s.id}`} onClick={() => goSection(s.id)} className="text-left py-3 text-base font-semibold text-[#121316] border-b border-[#E5E0D8]/60">
                {t(s.key)}
              </button>
            ))}
            <button data-testid="mnav-partner" onClick={() => { setOpen(false); navigate("/partner"); }} className="text-left py-3 text-base font-semibold text-[#121316]">
              {t("nav_partner")}
            </button>
            <button data-testid="mnav-join" onClick={() => { setOpen(false); navigate("/join"); }} className="text-left py-3 text-base font-semibold text-[#E05624]">
              {t("foot_join")}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
